import { Component } from '@theme/component';

/**
 * @typedef {Object} ContactFormRefs
 * @property {HTMLElement} [successMessage]
 * @property {HTMLElement} [errorMessage]
 */

/**
 * Progressively enhances the native contact form to submit via fetch instead of
 * a full-page POST-redirect-GET. The redirect flow relies on the follow-up GET
 * carrying enough session state to know a submission just succeeded, and that
 * state isn't reliably reflected in the redirected render - so the success
 * message never appears even though the submission itself goes through.
 * Submitting via fetch and reading the response directly sidesteps that.
 *
 * @extends {Component<ContactFormRefs>}
 */
class ContactForm extends Component {
  connectedCallback() {
    super.connectedCallback?.();
    this.#checkPostedSuccess();
  }

  /**
   * Shopify appends ?contact_posted=true to the redirect URL only after a
   * genuinely successful submission - including submissions that went through
   * the native form (e.g. after the user completed an hCaptcha challenge,
   * which happens entirely outside our fetch-based flow). Liquid's own
   * form.posted_successfully? check doesn't reliably reflect that state on
   * the redirected page, so this URL param is the one signal we can trust
   * regardless of which path the submission took.
   */
  #checkPostedSuccess() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('contact_posted') !== 'true') return;

    this.#showSuccess();

    // Prevent the message from reappearing on refresh/back navigation.
    url.searchParams.delete('contact_posted');
    window.history.replaceState({}, '', url);
  }

  /**
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;

    this.#hide(this.refs.successMessage);
    this.#hide(this.refs.errorMessage);

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
      });

      if (response.redirected && response.url.includes('/challenge')) {
        // Shopify decided this submission needs human verification (captcha).
        // That can't be solved silently via fetch, so send the browser there for real.
        window.location.href = response.url;
        return;
      }

      if (response.redirected) {
        // Any other redirect means a genuinely successful submission.
        this.#showSuccess();
        form.reset();
      } else if (response.ok) {
        // No redirect means the page was re-rendered in place with validation errors.
        const html = await response.text();
        this.#showError(this.#extractErrorText(html));
      } else {
        this.#showError();
      }
    } catch (error) {
      this.#showError();
    } finally {
      if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
    }
  }

  /**
   * @param {string} html
   * @returns {string | undefined}
   */
  #extractErrorText(html) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const remoteError = doc.querySelector('.contact-form__error [data-error-text]');
      const text = remoteError?.textContent?.trim();
      return text || undefined;
    } catch (error) {
      return undefined;
    }
  }

  #showSuccess() {
    const { successMessage } = this.refs;
    if (!successMessage) return;
    successMessage.hidden = false;
    successMessage.focus();
  }

  /** @param {string} [message] */
  #showError(message) {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;

    if (message) {
      const textTarget = errorMessage.querySelector('[data-error-text]');
      if (textTarget) textTarget.textContent = message;
    }

    errorMessage.hidden = false;
    errorMessage.focus();
  }

  /** @param {HTMLElement} [element] */
  #hide(element) {
    if (element) element.hidden = true;
  }
}

if (!customElements.get('contact-form-component')) {
  customElements.define('contact-form-component', ContactForm);
}
